import React, { useState } from 'react';
import { Dialog, Button } from 'components/ui';
import ExploreMedia from 'views/media/ExploreMedia';
import { getFileTypeImageVideo } from 'utils/fileType';
import { motion, AnimatePresence } from 'framer-motion';
import useDarkMode from 'utils/hooks/useDarkMode';
import { HiOutlinePhotograph, HiCheckCircle } from 'react-icons/hi';
import { TbPhotoStar } from 'react-icons/tb';

const ImageSelector = ({
    isOpen,
    onClose,
    onSelect,
    maxImages,
    allowedTypes = ["image"]
}) => {
    const [selectedAssets, setSelectedAssets] = useState([]);
    const [isDarkMode] = useDarkMode();

    const handleOnSelect = (original, asset) => {
        const newAsset = asset || {
            originalSrc: original,
            image: getFileTypeImageVideo(original) === "image" ? {
                url: original,
                thumbnail: {
                    url: original
                }
            } : undefined,
            video: getFileTypeImageVideo(original) === "video" ? {
                url: original
            } : undefined
        };

        const newSelectedAssets = [...selectedAssets, { originalSrc: original, asset: newAsset }];

        setSelectedAssets(newSelectedAssets);

        if (newSelectedAssets.length + 1 >= maxImages) {
            handleConfirm(newSelectedAssets);
        }

        return true;
    };

    const handleOnUnselect = (original, asset) => {
        setSelectedAssets(prev => 
            prev.filter(item => item.originalSrc !== original)
        );
        return true;
    };

    const handleConfirm = (_selectedAssets = null) => {
        onSelect(_selectedAssets || selectedAssets);
        setSelectedAssets([]);
        onClose();
    };

    const handleCancel = () => {
        setSelectedAssets([]);
        onClose();
    };

    return (
        <Dialog 
            isOpen={isOpen} 
            onClose={handleCancel} 
            onRequestClose={handleCancel} 
            className="!w-full"
            overlayClassName="flex items-center justify-center" 
            contentClassName="flex flex-col !h-[90vh] !overflow-hidden !p-0"
            width={1200} 
            closable={true}
        >
            <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="flex flex-col flex-1 overflow-hidden"
            >
                {/* Enhanced Header with Gradient Background */}
                <div className={`relative overflow-hidden px-6 pt-6 pb-4 ${
                    isDarkMode 
                        ? 'bg-gradient-to-br from-gray-900/50 via-gray-800/50 to-gray-900/50 border-b border-white/10' 
                        : 'bg-gradient-to-br from-white via-gray-50 to-white border-b border-gray-200'
                }`}>
                    {/* Decorative background elements */}
                    <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-full blur-3xl pointer-events-none" />
                    <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-blue-500/10 to-cyan-500/10 rounded-full blur-3xl pointer-events-none" />
                    
                    <div className="relative z-10 flex items-start justify-between gap-4">
                        <div className="flex items-center gap-4 flex-1">
                            <motion.div
                                initial={{ scale: 0, rotate: -180 }}
                                animate={{ scale: 1, rotate: 0 }}
                                transition={{ type: "spring", stiffness: 260, damping: 20 }}
                                className="shrink-0 w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-2xl shadow-purple-500/50"
                            >
                                <TbPhotoStar className="text-3xl text-white" />
                            </motion.div>
                            <div className="flex-1">
                                <h3 className={`text-xl font-black mb-1 ${
                                    isDarkMode ? 'text-white' : 'text-gray-900'
                                }`}>
                                    Select Images for Chat
                                </h3>
                                <p className={`text-sm ${
                                    isDarkMode ? 'text-gray-400' : 'text-gray-600'
                                }`}>
                                    Choose up to {maxImages} images to include in your conversation
                                </p>
                            </div>
                        </div>
                        
                        {/* Animated Selection Counter */}
                        <AnimatePresence>
                            {selectedAssets.length > 0 && (
                                <motion.div
                                    initial={{ scale: 0, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    exit={{ scale: 0, opacity: 0 }}
                                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                                    className={`shrink-0 px-4 py-3 rounded-xl backdrop-blur-xl shadow-lg -mt-2 ${
                                        isDarkMode
                                            ? 'bg-purple-500/10 border border-purple-500/20'
                                            : 'bg-purple-50 border border-purple-200'
                                    }`}
                                >
                                    <div className="flex items-center gap-2">
                                        <motion.div
                                            animate={{ rotate: [0, 360] }}
                                            transition={{ duration: 0.5 }}
                                            className={`p-1.5 rounded-lg ${
                                                isDarkMode ? 'bg-purple-500/20' : 'bg-purple-100'
                                            }`}
                                        >
                                            <HiCheckCircle className="text-lg text-purple-500" />
                                        </motion.div>
                                        <div>
                                            <p className={`text-xs font-medium ${
                                                isDarkMode ? 'text-gray-400' : 'text-gray-600'
                                            }`}>
                                                Selected
                                            </p>
                                            <p className={`text-lg font-black leading-none ${
                                                isDarkMode ? 'text-white' : 'text-gray-900'
                                            }`}>
                                                {selectedAssets.length} / {maxImages}
                                            </p>
                                        </div>
                                    </div>
                                    
                                    {/* Progress Bar */}
                                    <div className={`mt-2 h-1.5 rounded-full overflow-hidden ${
                                        isDarkMode ? 'bg-white/10' : 'bg-gray-200'
                                    }`}>
                                        <motion.div
                                            initial={{ width: 0 }}
                                            animate={{ width: `${(selectedAssets.length / maxImages) * 100}%` }}
                                            transition={{ type: "spring", stiffness: 100, damping: 15 }}
                                            className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full"
                                        />
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>

                {/* Media Explorer */}
                <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.1 }}
                    className="flex-1 overflow-y-auto px-6 pt-8"
                >
                    <ExploreMedia 
                        onClose={handleCancel}
                        allowedTypes={allowedTypes} 
                        isSelectable={true} 
                        isMultipleSelect={true}
                        showMyAssets={true} 
                        showAIAssets={true}
                        showStockAssets={true}
                        initialSelectedAssets={selectedAssets}
                        handleOnUnselect={handleOnUnselect}
                        handleOnSelect={handleOnSelect}
                        breakpoints={{ 350: 2, 512: 3, 750: 4, 900: 5, 1100: 6, 1600: 7 }}
                    />
                </motion.div>

                {/* Enhanced Footer */}
                <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className={`shrink-0 w-full bottom-0 left-0 right-0 flex justify-between items-center gap-4 px-6 py-4 border-t backdrop-blur-xl ${
                        isDarkMode 
                            ? 'border-white/10 bg-gray-900/80' 
                            : 'border-gray-200 bg-white/80'
                    }`}
                >
                    <motion.div 
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.3 }}
                        className={`flex items-center gap-2 text-sm font-medium ${
                            isDarkMode ? 'text-gray-400' : 'text-gray-600'
                        }`}
                    >
                        <HiOutlinePhotograph className="text-lg" />
                        <span>
                            {selectedAssets.length === 0 ? (
                                "No images selected"
                            ) : selectedAssets.length === 1 ? (
                                <span className={isDarkMode ? 'text-white' : 'text-gray-900'}>
                                    <strong>1</strong> image selected
                                </span>
                            ) : (
                                <span className={isDarkMode ? 'text-white' : 'text-gray-900'}>
                                    <strong>{selectedAssets.length}</strong> images selected
                                </span>
                            )}
                        </span>
                    </motion.div>
                    
                    <motion.div 
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.3 }}
                        className="flex gap-3"
                    >
                        <motion.div
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                        >
                            <Button 
                                variant="plain" 
                                onClick={handleCancel}
                                className="shadow-sm hover:shadow-md transition-all"
                            >
                                Cancel
                            </Button>
                        </motion.div>
                        <motion.div
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                        >
                            <Button 
                                variant="solid" 
                                onClick={() => handleConfirm()}
                                disabled={selectedAssets.length === 0}
                                className={`bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 !text-white shadow-lg hover:shadow-xl transition-all duration-200 ${
                                    selectedAssets.length === 0 ? 'opacity-50 cursor-not-allowed' : ''
                                }`}
                            >
                                <span className="flex items-center gap-2">
                                    <HiCheckCircle className="text-lg" />
                                    <span>
                                        Add {selectedAssets.length > 0 ? `${selectedAssets.length} ` : ''}Image{selectedAssets.length !== 1 ? 's' : ''}
                                    </span>
                                </span>
                            </Button>
                        </motion.div>
                    </motion.div>
                </motion.div>
            </motion.div>
        </Dialog>
    );
};

export default ImageSelector; 